/* ==========================================================================
  Dynamic Content
    -----------------------------------------------
    - open class for elements = .js-fetch
    - target content = '.js-fetch-content'
    - fetch container = '.js-fetch-container'
========================================================================== */

import { gsap, ScrollTrigger } from '../global/gsap';
import vars from '../global/vars.js';
import initLazyVideo from '../utilities/lazy-video';
import initLoadIn from '../utilities/load-in';

let contentOpen = false;

function initContent(content) {
    const loadInEls = content.querySelectorAll('.js-load-in');
    const lazyVideoEls = content.querySelectorAll('video.js-lazy');

    if (loadInEls && loadInEls.length > 0) {
        initLoadIn(loadInEls);
    }

    if (lazyVideoEls) {
        initLazyVideo(lazyVideoEls);
    }
}

function fetchContent(href, fetchContainer, fetchDestination, closeFetchButton, loader, popState = false) {
    vars.body.classList.add('working');
    loader.classList.add('show');

    fetch(href).then((response) => response.text()).then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Get content
        const content = doc.querySelector('.js-fetch-content');

        // If it exists, add it to DOM and return it, passed to next sequence
        if (content) {
            fetchDestination.appendChild(content);
            return content;
        }

        // If no content, stop everything
        return null;
    }).then((content) => {
        if (content) {
            // Set content as open and disable scroll
            contentOpen = true;
            vars.body.classList.add('stop-scrolling');

            // If content already exists in DOM els
            if (fetchDestination.children.length > 1) {
                // Set existing item
                const currentContent = fetchContainer.querySelector('.js-fetch-content:first-child');

                // Fade out destination DOM el and remove initial content, init new content
                gsap.to(fetchDestination, {
                    autoAlpha: 0,
                    duration: 0.33,
                    ease: 'power1.inOut',
                    onComplete: () => {
                        currentContent.remove();
                        initContent(fetchDestination);
                        ScrollTrigger.refresh();

                        vars.body.classList.remove('working');
                        loader.classList.remove('show');
                    }
                });

                // Fade destination DOM el back in
                gsap.to(fetchDestination, {
                    autoAlpha: 1,
                    duration: 0.33,
                    delay: 0.4,
                    ease: 'power1.inOut'
                });

            // Otherwise initial load
            } else {
                // Reveal main fetch container and init scripts on content
                gsap.to(fetchContainer, 0.4, {
                    autoAlpha: 1,
                    ease: 'power1.inOut',
                    onComplete: () => {
                        initContent(fetchDestination);
                        ScrollTrigger.refresh();
                        vars.body.classList.remove('working');
                        loader.classList.remove('show');

                        // gsap.to(cols, {
                        //     xPercent: 0,
                        //     stagger: 0.05,
                        //     duration: 0.35,
                        //     ease: 'power3.inOut'
                        // });
                    }
                });

                const cols = gsap.utils.toArray(fetchContainer.querySelectorAll('.js-fetch-col')).reverse();

                gsap.set(cols[0], {
                    xPercent: 65
                });
                gsap.set(cols[1], {
                    xPercent: 99.99,
                    opacity: 0
                });

                gsap.to(cols[0], {
                    xPercent: 0,
                    duration: 0.4,
                    ease: 'power3.inOut'
                });

                gsap.to(cols[1], {
                    opacity: 1,
                    duration: 0.2,
                    delay: 0.2
                });

                gsap.to(cols[1], {
                    xPercent: 0,
                    duration: 0.4,
                    ease: 'power3.inOut',
                    delay: 0.2
                });
            }
        }
    })
        .catch((err) => {
            // There was an error, remove any util classes
            vars.body.classList.remove('working');
            vars.body.classList.remove('stop-scrolling');
            loader.classList.remove('show');
            contentOpen = false;
            console.warn('Something went wrong.', err);
        });
}

function emptyContent(href, fetchContainer, fetchDestination, closeFetchButton, popState = false) {
    // Then hide container and clear destination DOM el
    gsap.to(fetchContainer, 0.4, {
        autoAlpha: 0,
        delay: 0.25,
        ease: 'power1.inOut',
        onComplete: () => {
            contentOpen = false;
            vars.body.classList.remove('stop-scrolling');
            fetchDestination.replaceChildren();
        }
    });
}

const initFetchContent = (fetchContainer, fetchLinks) => {
    const fetchDestination = fetchContainer.querySelector('.js-fetch-destination');
    const closeFetchButton = fetchContainer.querySelector('.js-fetch-close');
    const loader = document.querySelector('.js-loader');
    let originalHref = null;

    if (fetchContainer) {
        originalHref = fetchContainer.dataset.landing;
    }

    document.addEventListener('click', (event) => {
        const fetchLinkParent = event.target.closest('.js-fetch');

        // If fetch link child el or fetch link is clicked directly
        if (fetchLinkParent || [...fetchLinks].includes(event.target)) {
            event.preventDefault();

            let contentHref = null;

            if (event.target.href) {
                contentHref = event.target.href;
            } else {
                contentHref = fetchLinkParent.href;
            }

            fetchContent(contentHref, fetchContainer, fetchDestination, closeFetchButton, loader);
        }

        // If close button is clicked
        if (event.target === closeFetchButton) {
            event.preventDefault();
            emptyContent(originalHref, fetchContainer, fetchDestination, closeFetchButton);
        }
    });
};

export { initFetchContent as default };
